import React, { useEffect, useRef, useState, useCallback, Suspense, lazy } from 'react';
import Image from '@atlaskit/image';
import { ToastContainer, Slide, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { view } from '../../shims/forge-bridge';
import { Screens } from '../../assets';
import Loader from '../../components/Loader';
import Strings from '../../common/Strings';
import { LeftPanel, RightPanel, RootContainer, DefaultMessageContainer } from './MainScreen.styles';
import { useDataStore } from '../../providers';
import { JiraService } from '../../services';
import { APP_EVENTS, EpicsMap, MessageType, ServerError } from '../../common/Types';
import { UserStoreActionTypes } from '../../reducers';
import { AppStoreActionTypes } from '../../reducers/AppStore/AppStore.types';
import { isServerError, useThrottle } from '../../utils';
const HomeScreen = lazy(() => import('../HomeScreen'));
const GenerationPanel = lazy(() => import('../GenerationPanel'));
const ResultPanel = lazy(() => import('../ResultPanel/ResultPanel'));
import ToastContent from '../../components/ToastContent';
import {
  STICKY_HEADER_SCROLL_THRESHOLD,
  STICKY_HEADER_SCROLL_DEBOUCE_THRESHOLD
} from '../../common';

const MainScreen: React.FC = () => {
  const rootContainerRef = useRef<HTMLDivElement>(null);
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('dark');
  const [isChatPanelVisible, setIsChatPanelVisible] = useState(true);
  
  const toggleChatPanel = useCallback(() => {
    setIsChatPanelVisible(prev => !prev);
  }, []);
  
  const generationScrolCallBack = (scrollToTop = false) => {
    if (rootContainerRef?.current) {
      if (scrollToTop) rootContainerRef.current.scrollTop = 0;
      else rootContainerRef.current.scrollTop = rootContainerRef.current.scrollHeight;
    }
  };

  // Theme detection and synchronization with Jira
  useEffect(() => {
    const detectAndSetTheme = async () => {
      try {
        // Enable Forge theme synchronization
        await view.theme.enable();
        
        // Detect theme using media query (Forge syncs this automatically)
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        const themeMode = prefersDark ? 'dark' : 'light';
        
        setCurrentTheme(themeMode);
        document.documentElement.setAttribute('data-color-mode', themeMode);
      } catch (error) {
        setCurrentTheme('light');
        document.documentElement.setAttribute('data-color-mode', 'light');
      }
    };

    // Set initial theme
    detectAndSetTheme();

    // Listen for theme changes via media query
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = (event: MediaQueryListEvent) => {
      const themeMode = event.matches ? 'dark' : 'light';
      
      setCurrentTheme(themeMode);
      document.documentElement.setAttribute('data-color-mode', themeMode);
    };

    // Add event listener for theme changes
    mediaQuery.addEventListener('change', handleThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange);
    };
  }, []);

  useEffect(() => {
    // Register events
    document.addEventListener(APP_EVENTS.SCROLL_TO_TOP, () => generationScrolCallBack(true), false);
    document.addEventListener(
      APP_EVENTS.SCROLL_TO_BOTTOM,
      () => generationScrolCallBack(false),
      false
    );
    return () => {
      document.removeEventListener(APP_EVENTS.SCROLL_TO_TOP, () => generationScrolCallBack(true));
      document.removeEventListener(APP_EVENTS.SCROLL_TO_BOTTOM, () =>
        generationScrolCallBack(false)
      );
    };
  }, []);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const { state, dispatch } = useDataStore();
  const { isLoading } = state.appState;

  useEffect(() => {
    // set current project
    (async () => {
      try {
        const currentProject = await JiraService.getRecentProjects();
        if (!isServerError(currentProject)) {
          dispatch({
            type: UserStoreActionTypes.SetCurrentProject,
            payload: currentProject
          });
        }
      } catch (err) {
        toast.error(
          <ToastContent
            body={Strings.ERROR_MESSAGES.RECENT_PROJECT_FETCH_FAIL}
            type={MessageType.ERROR}
          />
        );
      }
    })();
  }, []);

  useEffect(() => {
    const { currentProject } = state.userStoryState;
    if (!currentProject || !currentProject.key || !state.appState.isHomePage) return;
    // Get Jira epics
    (async () => {
      const epics: EpicsMap | ServerError = await JiraService.loadEpics(currentProject);
      if (!isServerError(epics) && epics && Object.keys(epics).length) {
        dispatch({
          type: AppStoreActionTypes.SetLoading,
          payload: ''
        });
        dispatch({ type: UserStoreActionTypes.LoadJiraEpic, payload: epics });
        dispatch({
          type: AppStoreActionTypes.RemoveLoading
        });
      }
    })();
  }, [state.appState.isHomePage, state.userStoryState.currentProject]);

  const onScroll = () => {
    const scrollTop = rootContainerRef.current?.scrollTop || 0;
    setIsScrolled(scrollTop > STICKY_HEADER_SCROLL_THRESHOLD);
  };

  return (
    <div>
      <RootContainer
        className={`${isLoading && 'no-overflow'} ${isScrolled && 'scrolled'} ${!isChatPanelVisible && 'panel-collapsed'}`}
        ref={rootContainerRef}
        onScroll={useThrottle(onScroll, STICKY_HEADER_SCROLL_DEBOUCE_THRESHOLD)}>
        <LeftPanel className={!isChatPanelVisible ? 'expanded' : ''}>
          <Suspense fallback={<Loader />}>
            {state.appState.isHomePage ? <HomeScreen /> : (
              <ResultPanel 
                isChatPanelVisible={isChatPanelVisible} 
                onToggleChatPanel={toggleChatPanel} 
              />
            )}
          </Suspense>
        </LeftPanel>
        <RightPanel className={!isChatPanelVisible ? 'collapsed' : ''}>
          <Suspense fallback={<Loader />}>
            <GenerationPanel />
          </Suspense>
        </RightPanel>
      </RootContainer>
      <DefaultMessageContainer>
        <Image src={Screens} alt="Nebulas Logo" />
        <div>
          <div className="paddingContainer">{Strings.GREETING_TEXT}</div>
          <div className="paddingContainer">{Strings.MESSAGE}</div>
          <div className="paddingContainer">{Strings.SUBMESSAGE}</div>
          <div>{Strings.GREETS}</div>
          <div>{Strings.USERNAME}</div>
        </div>
      </DefaultMessageContainer>
      <ToastContainer
        transition={Slide}
        position={toast.POSITION.TOP_CENTER}
        autoClose={5000}
        icon={false}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={currentTheme}
      />
    </div>
  );
};

export default MainScreen;
